import { Link, useLocation } from "react-router-dom";

import { QcIncidentIssueTiersFields } from "@/components/qc/QcIncidentIssueTiersFields";
import { QcIncidentNotesThread } from "@/components/qc/QcIncidentNotesThread";
import { QcIncidentSummary } from "@/components/qc/QcIncidentSummary";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { CardTitleWithHelp } from "@/components/ui/title-help";
import { qcIncidentHomeFromPathname, qcIncidentListPath, qcIncidentPath } from "@/lib/qc/qcIncidentNav";
import type { QcIncident } from "@/pages/manufacturing/qcIncidentsApi";

export function QcIncidentViewModal({
  incident,
  writesAllowed,
  onClose,
  onResolve,
  onAnalysisLoan,
}: {
  incident: QcIncident;
  writesAllowed: boolean;
  onClose: () => void;
  onResolve?: () => void;
  onAnalysisLoan?: () => void;
}): JSX.Element {
  const location = useLocation();
  const canResolve = writesAllowed && incident.status === "unresolved";
  const onIncidentList = location.pathname === qcIncidentListPath(qcIncidentHomeFromPathname(location.pathname));

  return (
    <Card>
      <CardHeader>
        <CardTitleWithHelp hoverHint="Incident details. Add notes and set issue tiers here. Resolve and analysis loans stay separate actions.">
          QC incident #{incident.id}
        </CardTitleWithHelp>
      </CardHeader>
      <CardBody className="space-y-4">
        <QcIncidentSummary incident={incident} />
        <QcIncidentIssueTiersFields
          incident={incident}
          writesAllowed={writesAllowed}
          showTier1Editor
          showTier2Editor
        />
        <QcIncidentNotesThread incidentId={incident.id} writesAllowed={writesAllowed} />
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
          {onAnalysisLoan ? (
            <Button type="button" variant="secondary" onClick={onAnalysisLoan}>
              Analysis loan
            </Button>
          ) : null}
          {canResolve && onResolve ? (
            <Button type="button" onClick={onResolve}>
              Resolve incident
            </Button>
          ) : onIncidentList ? null : (
            <Link
              to={qcIncidentPath(incident.id, qcIncidentHomeFromPathname(location.pathname))}
              className="inline-flex h-9 items-center justify-center rounded-md border border-slate-300 bg-white px-4 text-sm font-medium text-slate-900 hover:bg-slate-50"
            >
              Open in QC Incidents
            </Link>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
